import React, { useContext, useState } from 'react';
import styled from 'styled-components';
import { useTranslation } from 'react-i18next';
import { SwipeableListItem } from '../../../../../../generic/swipe/SwipeableListAdapter';
import { SwipeAction } from '../../../../../../generic/ui/SwipeAction';
import { Mono } from '../../../../../../generic/ui/Mono';
import { Tag } from '../../../../../../generic/ui/Tag';
import Checkbox from '../../../knisot/Create/Components/Checkbox';
import { useResonDescByNum } from '../../../../../../generic/artcoded/resonslikut';
import { checkPackType } from '../../../../../../generic/utils/checkPackType';
import { EXIST_PATH } from '../../../../../../modules/API/settings';
import { LikutModals } from '../../../../../../modules/Context/CefiNum';
import MapPinIcon from '../../../../../../generic/icons/IconLib/MapPinIcon';
import PencilIcon from '../../../../../../generic/icons/IconLib/PencilIcon';
import ScaleIcon from '../../../../../../generic/icons/IconLib/ScaleIcon';
import BoxesIcon from '../../../../../../generic/icons/IconLib/BoxesIcon';
import PackageBoxIcon from '../../../../../../generic/icons/IconLib/PackageBoxIcon';
import SproutIcon from '../../../../../../generic/icons/IconLib/SproutIcon';

const PRODUCT_ID_COLOR = '#8CC53D';
const IMAGE_PLACEHOLDER = 'https://pngicon.ru/file/uploads/otkrytaja-korobka-128x85.png';

interface Props {
    item: any;
    index: number;
    isLast?: boolean;
    selecteble?: boolean;
    defaultPackageList: any[];
    isWeightCalculation: boolean;
    isAddIdToDesc: boolean;
    isOffice: boolean;
    isAllowMultipleSave: boolean;
    recID: number | null;
    setRecID: (id: number) => void;
    setGoodsCode: (code: string) => void;
    editOrderRow: (problem: number, reson: number) => void;
    selectGoodForLikut: (index: number) => void;
    handleSelectOrderLine: (recID: number) => void;
    handleOpenProductLocationModal: (product: any) => void;
    setDefaultPacks: (item: any) => void;
}

const LikutGoodsRow: React.FC<Props> = ({
    item,
    index,
    isLast = false,
    selecteble,
    defaultPackageList,
    isWeightCalculation,
    isAddIdToDesc,
    isOffice,
    isAllowMultipleSave,
    recID,
    setRecID,
    setGoodsCode,
    editOrderRow,
    selectGoodForLikut,
    handleSelectOrderLine,
    handleOpenProductLocationModal,
    setDefaultPacks,
}) => {
    const [t] = useTranslation(['likut']);
    const {
        ProductDesc, ImageUrl, OrderPacks, VarietyDesc, VarietyID,
        SupplyQty, PackID, RecID, OrderWeight, SupplyWeight,
        ChangeReason, ProductID, SizeID, Remarks,
        NoTagCollection, ProductCalculationMethod, isSelected,
    } = item;

    const reason = useResonDescByNum(+ChangeReason);
    const [resonState] = useContext<any>(LikutModals);
    const [isChecked, setIsChecked] = useState(!!isSelected);
    const [isOpenRemark, setIsOpenRemark] = useState(false);
    const [isOpenConfirmEmpty, setIsOpenConfirmEmpty] = useState(false);

    const disableSelectCheckBox =
        !isAllowMultipleSave ||
        (!isOffice && !NoTagCollection) ||
        (OrderPacks - SupplyQty) <= 0;

    const selectResult = checkPackType(defaultPackageList, PackID);
    const isWeightRest = isWeightCalculation || selectResult === '1';
    const rest = isWeightRest ? (OrderWeight - SupplyWeight).toFixed(2) : OrderPacks - SupplyQty;
    // units apply only to real יחידות (Pack_Type '2' or the NoTag piece-calculated case);
    // plain arizot get the standard single-box icon and no unit label
    const isNoTagUnits = isWeightCalculation && NoTagCollection && ProductCalculationMethod;
    const isKgUnit = !isNoTagUnits && isWeightRest;
    const isUnitsRest = isNoTagUnits || (!isKgUnit && selectResult === '2');
    const unitLabel = isKgUnit ? 'ק"ג' : isUnitsRest ? 'יח' : null;
    const restIcon = isKgUnit
        ? <ScaleIcon color='currentColor' size={14} />
        : isUnitsRest
            ? <BoxesIcon color='currentColor' size={14} />
            : <PackageBoxIcon color='currentColor' size={14} />;

    const cardTint = selectResult === '1' ? 'weight' as const
        : selectResult === '2' ? 'units' as const
            : undefined;

    const imageBg = ImageUrl === ''
        ? `url("${IMAGE_PLACEHOLDER}") center center no-repeat`
        : `url("${EXIST_PATH}${ImageUrl}") center center no-repeat`;

    const handleCheckboxChange = (value: boolean) => {
        setIsChecked(value);
        if (disableSelectCheckBox) return;
        handleSelectOrderLine(RecID);
    };

    const handleOpenProductLocation = (e: React.MouseEvent) => {
        e.stopPropagation();
        handleOpenProductLocationModal({
            ProductID: ProductID,
            VarietyID: VarietyID,
            SizeID: SizeID,
            PackID: PackID,
            Desc: `${SizeID?.trim()} ${ProductDesc?.trim()} ${VarietyDesc?.trim()}`,
        });
    };

    const handleCardClick = () => {
        if (selecteble) {
            setDefaultPacks(item);
            resonState?.action(true);
            resonState?.props?.setRowModal(false);
        }
    };

    return (
        <SwipeShell $isLast={isLast}>
            <SwipeableListItem
                swipeRight={{
                    content: (
                        <SwipeAction
                            variant='delete'
                            align='start'
                            label={t('likut_sectionmain_product_swiperight_text')}
                        />
                    ),
                    action: () => {
                        setRecID(RecID);
                        setIsOpenConfirmEmpty(!isOpenConfirmEmpty);
                    },
                }}
                swipeLeft={NoTagCollection || isOffice ? {
                    content: (
                        <SwipeAction
                            variant='edit'
                            label={t('likut_sectionmain_product_swipeleft_text')}
                        />
                    ),
                    action: () => {
                        setGoodsCode('');
                        selectGoodForLikut(index);
                    },
                } : undefined}
                threshold={0.35}
            >
                <ConfirmEmptyBtn
                    $visible={recID === RecID && isOpenConfirmEmpty}
                    onClick={() => {
                        editOrderRow(3, 3);
                        setIsOpenConfirmEmpty(!isOpenConfirmEmpty);
                    }}
                >
                    {t('likut_sectionmain_product_swiperight_button')}
                </ConfirmEmptyBtn>
                <Card
                    $tint={cardTint}
                    $interactive={!!selecteble}
                    onClick={handleCardClick}
                >
                    <ContentColumn>
                        <TitleLine>
                            <RowIconBtn
                                type='button'
                                onClick={handleOpenProductLocation}
                                aria-label='מיקום מוצר'
                                title='מיקום מוצר'
                            >
                                <MapPinIcon color='currentColor' size={14} />
                            </RowIconBtn>
                            <TitleIconSlot>
                                <SproutIcon color='currentColor' size={14} />
                            </TitleIconSlot>
                            <TitleText>
                                {SizeID?.trim()} {ProductDesc?.trim()} {VarietyDesc?.trim()}
                            </TitleText>
                            {isAddIdToDesc && (
                                <Tag variant='tinted' color={PRODUCT_ID_COLOR}>
                                    {ProductID}
                                </Tag>
                            )}
                           
                            {Remarks?.trim() !== '' && (
                                <RowIconBtn
                                    type='button'
                                    $danger
                                    onClick={(e) => {
                                        e.stopPropagation();
                                        setIsOpenRemark(!isOpenRemark);
                                    }}
                                    aria-label={t('likut_modal_remarks_title')}
                                    title={t('likut_modal_remarks_title')}
                                >
                                    <PencilIcon color='currentColor' size={14} />
                                </RowIconBtn>
                            )}
                        </TitleLine>
                        <RestLine>
                            <MetaLabel>{t('likut_sectionmain_product_rest_text')}</MetaLabel>
                            <RestIconSlot $kg={isKgUnit} $units={isUnitsRest}>{restIcon}</RestIconSlot>
                            <RestValue>{rest}</RestValue>
                            {unitLabel && <UnitLabel $kg={isKgUnit}>{unitLabel}</UnitLabel>}
                        </RestLine>
                        {isWeightCalculation && NoTagCollection ? (
                            <MetaLine>
                                <MetaLabel>{t('likut_sectionmain_product_rest_packages_text')}</MetaLabel>
                                <MetaValue>{OrderPacks - SupplyQty}</MetaValue>
                            </MetaLine>
                        ) : null}
                        {reason ? (
                            <MetaLine>
                                <MetaLabel>{t('likut_sectionmain_product_problem')}</MetaLabel>
                                <ReasonText>{reason}</ReasonText>
                            </MetaLine>
                        ) : null}
                        {isOpenRemark && Remarks?.trim() !== '' && (
                            <RemarkPanel>{Remarks?.trim()}</RemarkPanel>
                        )}
                    </ContentColumn>
                    {!disableSelectCheckBox && (
                        <CheckboxSlot onClick={(e) => e.stopPropagation()}>
                            <Checkbox
                                checked={isChecked}
                                onChange={handleCheckboxChange}
                            />
                        </CheckboxSlot>
                    )}
                    <ImageSlot style={{ background: imageBg, backgroundSize: 'contain' }} />
                </Card>
            </SwipeableListItem>
        </SwipeShell>
    );
};

export default LikutGoodsRow;

const SwipeShell = styled.div<{ $isLast: boolean }>`
    width: 100%;
    overflow-x: hidden;
    position: relative;
    border-bottom: ${({ theme, $isLast }) => ($isLast ? 'none' : `1px solid ${theme.colors.line2}`)};
`;

const ConfirmEmptyBtn = styled.button<{ $visible: boolean }>`
    display: ${({ $visible }) => ($visible ? 'flex' : 'none')};
    align-items: center;
    justify-content: center;
    min-width: 6rem;
    border: 0;
    background: ${({ theme }) => theme.colors.danger};
    color: #ffffff;
    font-family: ${({ theme }) => theme.fonts.sans};
    font-size: ${({ theme }) => theme.text.md};
    font-weight: 600;
    padding: 0 0.5rem;
    cursor: pointer;
`;

const Card = styled.div<{ $tint?: 'weight' | 'units'; $interactive: boolean }>`
    position: relative;
    width: 100%;
    min-height: 4.5rem;
    display: flex;
    align-items: center;
    gap: 0.75rem;
    direction: rtl;
    padding: 0.625rem 0.875rem;
    background: ${({ theme, $tint }) =>
        $tint === 'weight' ? theme.colors.successSoft
            : $tint === 'units' ? theme.colors.warningSoft
                : theme.colors.surface};
    cursor: ${({ $interactive }) => ($interactive ? 'pointer' : 'default')};
`;

const ImageSlot = styled.div`
    width: 2.75rem;
    height: 2.75rem;
    border-radius: 50%;
    background: ${({ theme }) => theme.colors.bg};
    flex-shrink: 0;
`;

const ContentColumn = styled.div`
    flex: 1;
    min-width: 0;
    display: flex;
    flex-direction: column;
    gap: 0.25rem;
`;

const TitleLine = styled.div`
    display: flex;
    align-items: center;
    gap: 0.375rem;
    min-width: 0;
    flex-wrap: wrap;
`;

const TitleText = styled.span`
    font-size: ${({ theme }) => theme.text.lg};
    font-weight: 600;
    color: ${({ theme }) => theme.colors.ink};
    font-family: ${({ theme }) => theme.fonts.sans};
    @media (max-width: ${({ theme }) => theme.breakpoints.mobile}) {
        font-size: ${({ theme }) => theme.textMobile.md};
    }
`;

const RowIconBtn = styled.button<{ $danger?: boolean }>`
    display: inline-flex;
    align-items: center;
    justify-content: center;
    width: 1.75rem;
    height: 1.75rem;
    border-radius: 0.5rem;
    border: 1px solid ${({ theme, $danger }) => ($danger ? theme.colors.danger : theme.colors.line)};
    background: ${({ theme }) => theme.colors.surface};
    color: ${({ theme, $danger }) => ($danger ? theme.colors.danger : theme.colors.icon)};
    cursor: pointer;
    flex-shrink: 0;
    transition: all ${({ theme }) => theme.motion.fast} ease;

    &:hover {
        border-color: ${({ theme, $danger }) => ($danger ? theme.colors.danger : theme.colors.inputBorderHover)};
    }
`;

const MetaLine = styled.div`
    display: flex;
    align-items: center;
    gap: 0.375rem;
`;

const MetaValue = styled(Mono)`
    font-size: ${({ theme }) => theme.text.sm};
    font-weight: 600;
    color: ${({ theme }) => theme.colors.ink2};
`;

const MetaLabel = styled.span`
    font-size: ${({ theme }) => theme.text.sm};
    color: ${({ theme }) => theme.colors.ink3};
    font-family: ${({ theme }) => theme.fonts.sans};
    white-space: nowrap;
`;

const ReasonText = styled.span`
    font-size: ${({ theme }) => theme.text.sm};
    font-weight: 600;
    color: ${({ theme }) => theme.colors.danger};
    font-family: ${({ theme }) => theme.fonts.sans};
`;

const RemarkPanel = styled.div`
    width: 100%;
    padding: 0.5rem;
    border: 1px solid ${({ theme }) => theme.colors.danger};
    border-radius: ${({ theme }) => theme.radii.md};
    background: ${({ theme }) => theme.colors.surface};
    color: ${({ theme }) => theme.colors.ink};
    font-size: ${({ theme }) => theme.text.sm};
    font-family: ${({ theme }) => theme.fonts.sans};
    white-space: pre-wrap;
    word-break: break-word;
    direction: rtl;
`;

const RestLine = styled.div`
    display: flex;
    align-items: center;
    gap: 0.375rem;
`;

const RestIconSlot = styled.span<{ $kg?: boolean; $units?: boolean }>`
    display: inline-flex;
    align-items: center;
    color: ${({ theme, $kg, $units }) =>
        $kg ? theme.colors.success : $units ? theme.colors.warning : theme.colors.ink3};
    flex-shrink: 0;
`;

const RestValue = styled(Mono)`
    font-size: ${({ theme }) => theme.text.lg};
    font-weight: 600;
    color: ${({ theme }) => theme.colors.ink};
    @media (max-width: ${({ theme }) => theme.breakpoints.mobile}) {
        font-size: ${({ theme }) => theme.textMobile.lg};
    }
`;

const UnitLabel = styled.span<{ $kg?: boolean }>`
    font-size: ${({ theme }) => theme.text.sm};
    font-weight: 600;
    color: ${({ theme, $kg }) => ($kg ? theme.colors.success : theme.colors.warning)};
    font-family: ${({ theme }) => theme.fonts.sans};
    white-space: nowrap;
`;

const TitleIconSlot = styled.span`
    display: inline-flex;
    align-items: center;
    color: ${({ theme }) => theme.colors.ink3};
    flex-shrink: 0;
`;

const CheckboxSlot = styled.div`
    flex-shrink: 0;
    display: inline-flex;
    align-items: center;
`;
